// src/shared/components/Layout/TenantSelector.tsx
// ────────────────────────────────────────────────────────────────────────────
// Sidebar-native tenant switcher — always-visible flat list.
//
// Matches the "Source Scopes → Tenants" pattern:
//   Tenants
//   ├─ All             ← default (JWT) — no override
//   ├─ system
//   └─ FinalFantasy
//
// Only renders for superadmin users. Clicking a tenant instantly switches
// the X-Tenant-Override header on all BFF requests.
// ────────────────────────────────────────────────────────────────────────────
import { useTenantOverride } from '@/shared/auth/tenantContext';
import { Building2 } from 'lucide-react';

export default function TenantSelector() {
  const {
    isSuperadmin,
    tenants,
    activeTenantId,
    ownTenantId,
    setOverride,
    loading,
  } = useTenantOverride();

  if (!isSuperadmin) return null;

  return (
    <div className="px-2 mb-3">
      {/* ── Section header ── */}
      <div className="flex items-center gap-1.5 px-2 pb-1.5 text-[10px] font-semibold
                      uppercase tracking-widest text-neutral-600 select-none">
        <Building2 size={11} className="flex-shrink-0" />
        Tenants
      </div>

      {/* ── Tenant list ── */}
      <div className="space-y-0.5 max-h-48 overflow-y-auto
                      scrollbar-thin scrollbar-thumb-neutral-800 scrollbar-track-transparent">

        {/* Individual tenants */}
        {tenants.map(t => {
          const isActive = activeTenantId === t.idTenant;
          const isOwn = t.idTenant === ownTenantId;

          return (
            <button
              key={t.idTenant}
              onClick={() => isOwn ? setOverride(null) : setOverride(t.idTenant)}
              className={`
                w-full text-left px-3 py-1.5 rounded border transition-colors
                ${isActive
                  ? 'pac-glass border-neon_orange-700/50 text-neon_orange-300'
                  : 'border-transparent text-neutral-500 hover:border-neutral-600 hover:text-neutral-300'
                }
              `}
            >
              <div className="text-xs font-medium leading-tight truncate">
                {t.tenantName ?? 'Tenant'}
              </div>
              <div className={`text-[10px] leading-tight font-mono ${isActive ? 'text-neon_orange-500/70' : 'text-neutral-600'}`}>
                {t.tenantRef}
              </div>
            </button>
          );
        })}

        {loading && (
          <div className="px-3 py-2 text-[10px] text-neutral-600 text-center animate-pulse">
            Loading…
          </div>
        )}
      </div>

      {/* ── Bottom separator ── */}
      <div className="h-px bg-white/5 mt-2" />
    </div>
  );
}
